import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { useLang } from "../lib/i18n";
import { HELP } from "../lib/helpContent";
import { useApp } from "../context/AppContext";
import { getTourExample } from "../lib/businessContext";

// Optional first-use guided tour (relaunchable via window event "lb-open-tour").
export function GuidedTour() {
  const { lang } = useLang();
  const { data } = useApp();
  const H = HELP[lang] || HELP.fr;
  const L = H.labels;
  // Connect the tour to the active business context: when a métier demo is running,
  // the Command Center step shows a concrete example coherent with that métier.
  const steps = React.useMemo(() => {
    const base = H.tour.map((s) => ({ ...s }));
    // Inject a métier-coherent example on the "take action" step when a demo profile is active.
    if (data?.demoProfileId) {
      const ci = base.findIndex((s) => s.cc);
      if (ci >= 0) base[ci] = { ...base[ci], text: `${base[ci].text}\n\n${getTourExample(data, lang)}` };
    }
    return base;
  }, [H, data, lang]);
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    // No auto-open: the "Par où commencer ?" guide replaces it. Tour stays available via "Bien démarrer".
    const onOpen = () => { setI(0); setOpen(true); };
    window.addEventListener("lb-open-tour", onOpen);
    return () => {
      window.removeEventListener("lb-open-tour", onOpen);
      // Safety: if this page unmounts (navigation) while the tour dialog is open,
      // Radix's modal lock (`body { pointer-events: none }` + overlay) can linger and
      // freeze the whole app. Restore interactivity so navigation never blocks.
      document.body.style.pointerEvents = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = () => { localStorage.setItem("lb_tour_done", "1"); setOpen(false); };
  const step = steps[i];
  const last = i === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="glass border-white/10 max-w-md" data-testid="guided-tour">
        <DialogHeader>
          <DialogTitle className="sr-only">{H.tourTitle}</DialogTitle>
          <DialogDescription className="sr-only">{step?.text}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center mb-1">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-neon flex items-center justify-center text-white">
            {last ? <Check size={22} /> : <Sparkles size={22} />}
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="text-center">
            <h2 className="font-display font-bold text-xl mb-2">{step?.title}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed px-1 whitespace-pre-line">{step?.text}</p>
          </motion.div>
        </AnimatePresence>
        <div className="flex justify-center gap-1.5 my-3">
          {steps.map((_, idx) => (
            <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-neon" : "w-1.5 bg-white/15"}`} />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2">
          <button data-testid="tour-skip" onClick={finish} className="inline-flex items-center min-h-[44px] px-2 -ml-2 sm:min-h-0 sm:px-0 sm:ml-0 text-xs text-[hsl(var(--muted-foreground))] hover:text-white transition-colors">{L.skip}</button>
          <div className="flex gap-2">
            {i > 0 && (
              <Button data-testid="tour-prev" variant="outline" onClick={() => setI(i - 1)} className="rounded-full border-white/10 bg-white/5 h-11 sm:h-10 px-4 text-sm">
                <ArrowLeft size={15} className="mr-1" /> {L.prev}
              </Button>
            )}
            {last ? (
              <Button data-testid="tour-finish" onClick={finish} className="rounded-full h-11 sm:h-10 px-5 text-sm bg-gradient-to-r from-primary to-neon text-white lb-glow-btn">
                {L.finish} <Check size={15} className="ml-1" />
              </Button>
            ) : (
              <Button data-testid="tour-next" onClick={() => setI(i + 1)} className="rounded-full h-11 sm:h-10 px-5 text-sm bg-gradient-to-r from-primary to-neon text-white lb-glow-btn">
                {L.next} <ArrowRight size={15} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
